"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableRow = exports.TableHeaderRow = exports.TableHeaderCell = exports.TableFooterRow = exports.TableFooterCell = exports.TableCell = exports.TableBody = exports.Table = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const useAvoidIsMobile_1 = require("../../hooks/useAvoidIsMobile");
const Button_1 = require("../Button");
const TableContext = react_1.default.createContext({
    alignment: "center",
    hasHeaderRow: false,
    hasHeaderCol: false,
    hasFooterRow: false,
    columnCount: 0,
    defaultGrow: 1,
    selectedColumn: 0,
    setSelectedColumn: () => { },
    enableMobileButtons: false,
    isMobile: false
});
const TableMobileControl = ({ buttons, columnCount }) => {
    const { selectedColumn, setSelectedColumn } = react_1.default.useContext(TableContext);
    const handleClick = (col) => setSelectedColumn(col);
    const colGap = columnCount - buttons.length;
    return (react_1.default.createElement(foundations_1.Box, { sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            alignContent: "flex-start",
            gap: 2,
            flexWrap: "wrap"
        } }, buttons.map((button, i) => {
        const num = i + colGap;
        return button ? (react_1.default.createElement(Button_1.Button, { variant: num === selectedColumn ? "primary" : "secondary", key: num, onClick: () => handleClick(num) }, button)) : null;
    })));
};
const Table = (props) => {
    const { alignment = "center", children, headerButtonTitles = [], useMobileButtons = false, defaultVisibleColumnIndex } = props;
    let hasHeaderRow = false;
    let hasFooterRow = false;
    let hasHeaderCol = false;
    let columnCount = 0;
    const [selectedColumn, setSelectedColumn] = react_1.default.useState(defaultVisibleColumnIndex || 0);
    const isMobile = (0, useAvoidIsMobile_1.useAvoidIsMobile)();
    const [enableMobileButtons, setEnableMobileButtons] = react_1.default.useState(useMobileButtons);
    react_1.default.useEffect(() => {
        setEnableMobileButtons(isMobile && useMobileButtons);
    }, [isMobile, useMobileButtons]);
    react_1.default.Children.forEach(children, (child) => {
        if (child.type === TableHeaderRow) {
            hasHeaderRow = true;
        }
        if (child.type === TableFooterRow) {
            hasFooterRow = true;
        }
        if (child.type === TableBody) {
            react_1.default.Children.forEach(child.props.children, (row) => {
                react_1.default.Children.forEach(row.props.children, (cell) => {
                    if (cell.type === TableHeaderCell) {
                        hasHeaderCol = true;
                    }
                });
                if (columnCount === 0) {
                    columnCount = react_1.default.Children.count(row.props.children);
                }
            });
        }
    });
    const defaultGrow = 1 / columnCount;
    const context = {
        alignment,
        hasHeaderRow,
        hasHeaderCol,
        hasFooterRow,
        columnCount,
        defaultGrow,
        selectedColumn,
        setSelectedColumn,
        enableMobileButtons,
        isMobile
    };
    return (react_1.default.createElement(TableContext.Provider, { value: context },
        react_1.default.createElement(foundations_1.Box, { sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 3
            } },
            enableMobileButtons && headerButtonTitles.length > 0 && (react_1.default.createElement(TableMobileControl, { buttons: headerButtonTitles, columnCount: columnCount })),
            react_1.default.createElement(foundations_1.Box, { as: "div", sx: {
                    overflowX: "auto",
                    boxShadow: "boxShadow01",
                    borderRadius: "tableBorderRadius",
                    display: "block",
                    width: "100%"
                } },
                react_1.default.createElement(foundations_1.Box, { as: "table", sx: {
                        borderCollapse: "separate",
                        border: (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableBorder}`; },
                        borderRadius: "tableBorderRadius",
                        tableLayout: "fixed",
                        minWidth: "100%"
                    } }, children)))));
};
exports.Table = Table;
const TableHeaderRow = ({ children }) => {
    return (react_1.default.createElement(foundations_1.Box, { as: "thead", sx: {
            width: "100%",
            // @ts-ignore
            "tr:first-of-type th:first-of-type": {
                borderTopLeftRadius: "tableBorderRadius"
            },
            "tr:first-of-type th:last-child": {
                borderTopRightRadius: "tableBorderRadius",
                borderRight: "none"
            }
        } }, children));
};
exports.TableHeaderRow = TableHeaderRow;
const TableHeaderCell = ({ alignment, grow, children, isSelected = false, isAlwaysVisible = false }) => {
    const { defaultGrow, enableMobileButtons, isMobile } = react_1.default.useContext(TableContext);
    if (grow === undefined) {
        grow = defaultGrow;
    }
    if (grow < 0 || grow > 1) {
        throw new Error("grow property must be a number between 0 and 1.");
    }
    return (react_1.default.createElement(foundations_1.Box, { as: "th", sx: {
            borderBottom: (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableHeaderBorder}`; },
            borderRight: enableMobileButtons && isSelected && isMobile
                ? "unset"
                : (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableHeaderBorder}`; },
            alignContent: "center",
            backgroundColor: "tableHeaderBackgroundColor",
            width: () => `${(grow || defaultGrow) * 100}%`,
            display: isSelected || isAlwaysVisible ? "table-cell" : "none"
        } },
        react_1.default.createElement(TableCellWrapper, { alignmentOverride: alignment }, children)));
};
exports.TableHeaderCell = TableHeaderCell;
const TableBody = ({ children }) => {
    const { hasHeaderRow, hasFooterRow, hasHeaderCol } = react_1.default.useContext(TableContext);
    return (react_1.default.createElement(foundations_1.Box, { as: "tbody", sx: {
            // @ts-ignore
            "tr:nth-of-type(even)": {
                backgroundColor: "tableBodyEvenBackgroundColor"
            },
            "tr:nth-of-type(odd)": {
                backgroundColor: "tableBodyOddBackgroundColor"
            },
            "tr:last-child td": {
                borderBottom: hasFooterRow ? "tableBorderRadius" : "unset"
            },
            "tr:first-of-type td:first-of-type": {
                borderTopLeftRadius: hasHeaderRow ? "unset" : "tableBorderRadius"
            },
            "tr:first-of-type td:last-child": {
                borderTopRightRadius: hasHeaderRow ? "unset" : "tableBorderRadius"
            },
            "tr:last-child td:first-of-type": {
                borderBottomLeftRadius: hasFooterRow || hasHeaderCol ? "unset" : "tableBorderRadius"
            },
            "tr:last-child th:first-of-type": {
                borderBottomLeftRadius: hasFooterRow ? "unset" : "tableBorderRadius"
            },
            "tr:last-child td:last-child": {
                borderBottomRightRadius: hasFooterRow ? "unset" : "tableBorderRadius"
            }
        } }, children));
};
exports.TableBody = TableBody;
const TableRow = ({ children }) => {
    const { selectedColumn, enableMobileButtons } = react_1.default.useContext(TableContext);
    return (react_1.default.createElement(foundations_1.Box, { as: "tr", sx: {
            width: "100%",
            // @ts-ignore
            "td:last-child": {
                borderRight: "none"
            }
        } }, react_1.default.Children.map(children, (child, index) => {
        if (react_1.default.isValidElement(child)) {
            const showCell = !enableMobileButtons
                ? true
                : index === selectedColumn
                    ? true
                    : false;
            return react_1.default.cloneElement(child, { isSelected: showCell });
        }
        return child;
    })));
};
exports.TableRow = TableRow;
const TableCellWrapper = ({ alignmentOverride, children }) => {
    const { alignment } = react_1.default.useContext(TableContext);
    const newAlignment = alignmentOverride ? alignmentOverride : alignment;
    return (react_1.default.createElement(foundations_1.Box, { as: "span", sx: {
            gap: 2,
            display: "flex",
            px: 6,
            py: 4,
            justifyContent: newAlignment
        } }, children));
};
const TableCell = ({ grow, alignment, children, isSelected = false, isAlwaysVisible = false }) => {
    const { defaultGrow, enableMobileButtons, isMobile } = react_1.default.useContext(TableContext);
    if (grow === undefined) {
        grow = defaultGrow;
    }
    if (grow < 0 || grow > 1) {
        throw new Error("grow property must be a number between 0 and 1.");
    }
    return (react_1.default.createElement(foundations_1.Box, { as: "td", sx: {
            borderBottom: (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableBodyBorder}`; },
            borderRight: enableMobileButtons && isSelected && isMobile
                ? "unset"
                : (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableBodyBorder}`; },
            width: () => `${(grow || defaultGrow) * 100}%`,
            display: isSelected || isAlwaysVisible ? "table-cell" : "none"
        } },
        react_1.default.createElement(TableCellWrapper, { alignmentOverride: alignment }, children)));
};
exports.TableCell = TableCell;
const TableFooterRow = ({ children }) => {
    return (react_1.default.createElement(foundations_1.Box, { as: "tfoot", sx: {
            width: "100%",
            // @ts-ignore
            "tr:last-of-type th:first-of-type": {
                borderBottomLeftRadius: "tableBorderRadius"
            },
            "tr:last-of-type th:last-child": {
                borderBottomRightRadius: "tableBorderRadius",
                borderRight: "none"
            }
        } }, children));
};
exports.TableFooterRow = TableFooterRow;
const TableFooterCell = ({ alignment, children, isSelected = false, isAlwaysVisible = false }) => {
    const { enableMobileButtons, isMobile } = react_1.default.useContext(TableContext);
    return (react_1.default.createElement(foundations_1.Box, { as: "th", sx: {
            borderBottom: (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableFooterBorder}`; },
            borderRight: enableMobileButtons && isSelected && isMobile
                ? "unset"
                : (theme) => { var _a; return `${(_a = theme === null || theme === void 0 ? void 0 : theme.borders) === null || _a === void 0 ? void 0 : _a.tableFooterBorder}`; },
            alignContent: "center",
            backgroundColor: "tableFooterBackgroundColor",
            display: isSelected || isAlwaysVisible ? "table-cell" : "none"
        } },
        react_1.default.createElement(TableCellWrapper, { alignmentOverride: alignment }, children)));
};
exports.TableFooterCell = TableFooterCell;
//# sourceMappingURL=Table.js.map